import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeImageForUpload } from '@/lib/imageResize';
import { dayKey, formatDayLabel, formatMessageTime } from '@/lib/chatFormat';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ImagePlus, Loader2, Package, Send, X } from 'lucide-react';

const POLL_MS = 3000;
const MAX_IMAGE_CHARS = 1_500_000;
const MAX_RAW_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE = /^data:image\/(jpeg|png);base64,/;

function mergeMessages(current, incoming) {
  const seen = new Set(current.map((m) => m.id));
  const added = incoming.filter((m) => !seen.has(m.id));
  return added.length ? [...current, ...added] : current;
}

/**
 * One support conversation: the messages (polled), and a box to write a
 * message or add a photo. Used by the app's Support screen and by the
 * /admin portal; the caller supplies how to load, send and mark read.
 *
 *   load(after)  -> { messages }       (all latest, or newer than `after`)
 *   send(draft)  -> message            draft = { body, image, productId }
 *   markRead()
 *   attachment   optional product chip sent with the next message
 *   senderLabel(m) optional small name above a message
 *   allowPhotos  false hides the photo button (produce chats are text only)
 *   renderExtra(m) optional card shown above a message's text (e.g. the
 *                produce an offtaker asked about)
 *   initialDraft text already typed in the box (the user still presses Send)
 */
export default function SupportThread({
  threadKey,
  load,
  send,
  markRead,
  attachment,
  onClearAttachment,
  senderLabel,
  emptyText,
  composerClassName = 'fixed bottom-16 inset-x-0 z-40',
  messagesClassName = '',
  onProductOpen,
  allowPhotos = true,
  renderExtra,
  initialDraft = '',
}) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(initialDraft);
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const lastAtRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const absorb = useCallback((incoming) => {
    if (!incoming.length) return;
    lastAtRef.current = incoming[incoming.length - 1].created_at;
    setMessages((current) => mergeMessages(current, incoming));
    if (incoming.some((m) => !m.mine)) void markRead();
  }, [markRead]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setMessages([]);
    lastAtRef.current = null;
    (async () => {
      try {
        const res = await load(null);
        if (cancelled) return;
        absorb(res.messages || []);
        void markRead();
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [threadKey, load, absorb, markRead]);

  useEffect(() => {
    if (loading || failed) return undefined;
    const timer = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await load(lastAtRef.current);
        absorb(res.messages || []);
      } catch {
        // Try again on the next tick.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [threadKey, loading, failed, load, absorb]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_RAW_BYTES) {
      toast({ title: t('chat.photoTooBig'), variant: 'destructive' });
      return;
    }
    try {
      const dataUrl = await resizeImageForUpload(file, { maxEdge: 1280, quality: 0.8 });
      if (!ALLOWED_IMAGE.test(dataUrl) || dataUrl.length > MAX_IMAGE_CHARS) {
        toast({ title: t('chat.photoTooBig'), variant: 'destructive' });
        return;
      }
      setPhoto(dataUrl);
    } catch {
      toast({ title: t('chat.photoFailed'), variant: 'destructive' });
    }
  };

  const submit = async () => {
    const body = draft.trim();
    if ((!body && !photo && !attachment) || sending) return;
    setSending(true);
    try {
      const message = await send({ body, image: photo, productId: attachment?.id });
      absorb([message]);
      setDraft('');
      setPhoto(null);
      onClearAttachment?.();
    } catch {
      toast({ title: t('chat.sendFailed'), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }
  if (failed) {
    return <p className="p-6 text-center text-muted-foreground">{t('supportChat.loadFailed')}</p>;
  }

  let lastDay = null;
  return (
    <>
      <div className={`px-3 py-3 space-y-2 ${messagesClassName}`} data-testid="support-messages">
        {messages.length === 0 && emptyText && (
          <p className="text-center text-sm text-muted-foreground py-10 px-6" data-testid="support-empty">
            {emptyText}
          </p>
        )}
        {messages.map((m) => {
          const day = dayKey(m.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          const label = senderLabel?.(m);
          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <p className="text-center text-[11px] text-muted-foreground py-2">
                  {formatDayLabel(m.created_at, i18n.language, t)}
                </p>
              )}
              <div className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 shadow-sm ${
                    m.mine ? 'bg-farm-primary text-white rounded-br-sm' : 'bg-white text-farm-forest rounded-bl-sm'
                  }`}
                  data-testid={m.mine ? 'message-mine' : 'message-theirs'}
                >
                  {label && (
                    <p className={`text-[11px] font-semibold mb-0.5 ${m.mine ? 'text-white/80' : 'text-farm-primary'}`}>
                      {label}
                    </p>
                  )}
                  {renderExtra?.(m)}
                  {m.product && (
                    <ProductCard product={m.product} mine={m.mine} t={t} onOpen={onProductOpen} />
                  )}
                  {m.image && (
                    <button type="button" onClick={() => setLightbox(m.image)} className="block mb-1">
                      <img src={m.image} alt={t('chat.photo')} className="rounded-xl max-h-64 object-cover" />
                    </button>
                  )}
                  {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                  <p className={`text-[10px] mt-1 text-right ${m.mine ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {formatMessageTime(m.created_at, i18n.language)}
                  </p>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className={`${composerClassName} bg-white border-t border-gray-200`}>
        <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto px-3 py-2">
          {(photo || attachment) && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {attachment && (
                <span className="inline-flex items-center gap-1.5 bg-farm-primary/10 text-farm-forest text-xs font-medium rounded-full pl-2.5 pr-1 py-1" data-testid="support-attachment">
                  <Package className="w-3.5 h-3.5" aria-hidden />
                  {attachment.name}
                  <button type="button" onClick={onClearAttachment} aria-label={t('chat.removePhoto')} className="p-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {photo && (
                <span className="relative inline-block">
                  <img src={photo} alt="" className="h-16 rounded-lg" />
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    className="absolute -top-2 -right-2 bg-white rounded-full shadow p-0.5"
                    aria-label={t('chat.removePhoto')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={pickPhoto}
              data-testid="support-photo-input"
            />
            {allowPhotos && (
              <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} aria-label={t('chat.addPhoto')}>
                <ImagePlus className="w-5 h-5 text-farm-primary" />
              </Button>
            )}
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              maxLength={2000}
              placeholder={t('chat.placeholder')}
              aria-label={t('chat.placeholder')}
              className="flex-1 resize-none max-h-32 border border-gray-300 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-farm-primary"
              data-testid="support-input"
            />
            <Button
              size="icon"
              className="bg-farm-primary rounded-full"
              onClick={() => void submit()}
              disabled={sending || (!draft.trim() && !photo && !attachment)}
              aria-label={t('chat.send')}
              data-testid="support-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogTitle className="sr-only">{t('chat.photo')}</DialogTitle>
          {lightbox && <img src={lightbox} alt="" className="w-full h-auto rounded-lg" />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProductCard({ product, mine, t, onOpen }) {
  const name = (
    <span className="inline-flex items-center gap-2 text-sm font-semibold">
      <Package className="w-4 h-4 flex-shrink-0" aria-hidden />
      {product.name}
    </span>
  );
  return (
    <div className={`rounded-xl p-2 mb-1 ${mine ? 'bg-white/15' : 'bg-farm-primary/5'}`} data-testid="support-product-card">
      <p className={`text-[11px] font-semibold uppercase tracking-wide ${mine ? 'text-white/80' : 'text-farm-primary'}`}>
        {t('supportChat.aboutProduct')}
      </p>
      {onOpen ? (
        <button type="button" onClick={() => onOpen(product)} className="mt-1 text-left hover:underline underline-offset-2">
          {name}
        </button>
      ) : (
        <div className="mt-1">{name}</div>
      )}
    </div>
  );
}
